<template>
  <v-card class="tarjeta">
    <!-- FOTO: marco con proporcion fija para que el spinner ocupe exactamente
         el mismo alto que la imagen y la tarjeta no pegue un salto al cargar -->
    <div class="marco-foto">
      <div v-if="cargandoImagen" class="capa-carga">
        <v-progress-circular indeterminate color="primary" size="30" width="3" />
      </div>
      <v-img
        v-else
        :src="foto"
        cover
        :aspect-ratio="16 / 9"
        class="foto"
      >
        <!-- La promesa ya resolvio, pero al navegador todavia le falta pintar
             el base64. Sin esto se ve un hueco blanco en ese instante. -->
        <template #placeholder>
          <div class="capa-carga">
            <v-progress-circular indeterminate color="primary" size="30" width="3" />
          </div>
        </template>
      </v-img>

      <div v-if="!cargandoImagen && !tieneFoto" class="sin-foto">Sin fotos</div>
    </div>

    <!-- DATOS -->
    <div class="cuerpo">
      <template v-if="cargandoDatos">
        <div class="esqueleto esqueleto-titulo"></div>
        <div class="esqueleto esqueleto-linea"></div>
        <div class="esqueleto esqueleto-linea corta"></div>
      </template>

      <template v-else-if="error">
        <div class="titulo">Empresa #{{ idEmpresa }}</div>
        <div class="aviso-error">
          <v-icon size="16">mdi-alert-circle-outline</v-icon>
          No se pudieron cargar los datos
        </div>
        <button class="reintentar" @click="cargarDatos">Reintentar</button>
      </template>

      <template v-else>
        <div class="titulo">{{ empresa.company }}</div>

        <div v-if="ubicacion" class="linea">
          <v-icon size="15">mdi-map-marker-outline</v-icon>
          <span>{{ ubicacion }}</span>
        </div>

        <div v-if="empresa.phone" class="linea">
          <v-icon size="15">mdi-phone-outline</v-icon>
          <span>{{ empresa.phone }}</span>
        </div>

        <p v-if="empresa.description" class="descripcion">{{ empresa.description }}</p>

        <div class="vistas">
          <v-icon size="15">mdi-eye-outline</v-icon>
          {{ empresa.views || 0 }} {{ empresa.views === 1 ? 'vista' : 'vistas' }}
        </div>
      </template>
    </div>

    <!-- ACCIONES: no dependen de la carga, solo necesitan el id que ya tenemos -->
    <div class="acciones">
      <v-btn
        variant="text"
        class="accion"
        @click="$router.push({ name: 'ViewCompanyView', params: { id: idEmpresa } })"
      >
        <v-icon size="18">mdi-eye</v-icon>
        Ver
      </v-btn>
      <v-btn
        variant="text"
        class="accion accion-principal"
        @click="$router.push({ name: 'EditCompanyView', params: { id: idEmpresa } })"
      >
        <v-icon size="18">mdi-pencil</v-icon>
        Editar
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import apisCompanys from '@/apis/apisCompanys'
import imagenPorDefecto from '@/assets/imagen-none.jpg'

/**
 * Tarjeta de una empresa del usuario.
 *
 * Recibe SOLO el id y se encarga de todo lo demas ella misma. La vista que la
 * usa no pide datos ni imagenes: cada tarjeta carga a su ritmo, asi que una
 * empresa con una foto pesada no retrasa a las demas.
 *
 * Orden a proposito: primero los datos generales, y SOLO cuando llegan se pide
 * la imagen. El texto es liviano y aparece casi de una; la foto viaja en base64
 * y es lo pesado, asi que se deja de ultimo para que no le quite turno al texto.
 */
export default {
  name: 'TarjetaEdicionEmpresa',
  props: {
    idEmpresa: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      empresa: null,
      cargandoDatos: true,
      error: false,
      imagen: null,
      cargandoImagen: true
    }
  },
  computed: {
    tieneFoto() {
      return this.imagen !== null
    },
    foto() {
      return this.imagen || imagenPorDefecto
    },
    ubicacion() {
      return [this.empresa?.city, this.empresa?.country].filter(Boolean).join(', ')
    }
  },
  created() {
    this.cargarDatos()
  },
  methods: {
    cargarDatos() {
      this.cargandoDatos = true
      this.error = false

      return apisCompanys.getCompany(this.idEmpresa)
        .then(empresa => {
          this.empresa = empresa
          this.cargandoDatos = false

          // Encadenada, no en paralelo: la foto arranca cuando el texto ya esta.
          return this.cargarImagen()
        })
        .catch(() => {
          this.error = true
          this.cargandoDatos = false
          // Si los datos fallaron no se pide la imagen, pero hay que apagar su
          // spinner igual: si no, la tarjeta se queda girando para siempre.
          this.cargandoImagen = false
        })
    },
    cargarImagen() {
      this.cargandoImagen = true

      return apisCompanys.ImagesCompany(this.idEmpresa)
        .then(imagenes => {
          this.imagen = imagenes && imagenes.length > 0 ? imagenes[0].pathBase64 : null
        })
        .catch(() => {
          // Que no haya foto no es motivo para romper la tarjeta
          this.imagen = null
        })
        .finally(() => {
          this.cargandoImagen = false
        })
    }
  }
}
</script>

<style scoped>
.tarjeta {
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.tarjeta:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.16);
  transform: translateY(-2px);
}

/* FOTO */
.marco-foto {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  /* Sin esto el aspect-ratio se lo come el min-height: auto del flex en columna
     y el marco toma el alto natural de la imagen */
  min-height: 0;
  background: #eceff1;
}
.foto {
  width: 100%;
  height: 100%;
}
.capa-carga {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eceff1;
}
.sin-foto {
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 0.68rem;
  letter-spacing: 0.03em;
}

/* CUERPO */
.cuerpo {
  flex: 1;
  padding: 12px 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.titulo {
  font-size: 1.02rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.linea {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #667085;
}
.linea span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.descripcion {
  margin: 4px 0 0;
  font-size: 0.82rem;
  color: #475467;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.vistas {
  margin-top: auto;
  padding-top: 8px;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.76rem;
  color: #98a2b3;
}

/* ESTADO DE CARGA DEL TEXTO */
.esqueleto {
  border-radius: 6px;
  background: linear-gradient(90deg, #eceff1 25%, #f5f7f8 50%, #eceff1 75%);
  background-size: 200% 100%;
  animation: brillo 1.2s ease-in-out infinite;
}
.esqueleto-titulo {
  height: 17px;
  width: 72%;
  margin-bottom: 6px;
}
.esqueleto-linea {
  height: 11px;
  width: 90%;
}
.esqueleto-linea.corta {
  width: 55%;
}
@keyframes brillo {
  from { background-position: 200% 0; }
  to   { background-position: -200% 0; }
}

/* ERROR */
.aviso-error {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #b42318;
}
.reintentar {
  align-self: flex-start;
  margin-top: 6px;
  padding: 3px 10px;
  border: 1px solid #1976d2;
  border-radius: 6px;
  background: none;
  color: #1976d2;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
}
.reintentar:hover {
  background: #e8f1fb;
}

/* ACCIONES */
.acciones {
  display: flex;
  border-top: 1px solid #eceff1;
}
.accion {
  flex: 1;
  border-radius: 0;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  color: #667085;
  gap: 5px;
}
.accion-principal {
  color: #1976d2;
}
</style>
